import * as React from 'react';
import { IApplicationState, RecipientActionCreators, AppActionCreators, ICompatibilityResultsResponse } from '@nkr/store/index';
import { ISingleEpletMmRisk, IPotentialDonor, IRecipientResponse, IRecipient, ICompatibilityResult, IBaseResponse, ILkdpiInfo, IRecipientWithPairedDonor } from '@nkr/models/index';
import { PatientType } from '@nkr/enums/index';
import { Dispatch, connect } from 'react-redux';
import ConfirmWaitlistReplace from './confirm-waitlist-replace-modal';
import WaitlistReplaceCurrentDonor from './waitlist-replace-current-donor';
import RecipientNav from '@nkr/views/recipients/recipient-nav';
import WaitListGrid from './waitlist-grid';
import PairGrid from './pair-grid';
import ConfirmPairRequest from './confirm-pair-request-modal';
import { toast } from 'react-toastify';
import ICompatibilityResultWithEpletMmRisk from './compatibility-result-with-eplet-mm-risk';

interface IProps {
    centerId: number,
    recipientId: number
}

interface IStateProps {
    loading: boolean
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getRecipient: (recipientId?: number) => Promise<IRecipientResponse>,
    getWaitlistReplacements: (centerId: number, recipientId?: number) => Promise<ICompatibilityResultsResponse>
    replaceWaitlistRecipient: (centerId: number, placeHolderRecpId: number, selectedRecpId: number) => Promise<IBaseResponse>,
    requestReplaceWaitlistRecipientWithPair: (centerId: number, originalRecipientId: number, replacementRecipientId: number, bridgeDonorId: number) => Promise<IBaseResponse>
}

type TProps = IStateProps & IDispatchProps & IProps;

interface IState {
    recipient: IRecipient,
    donor: IPotentialDonor,
    waitList: ICompatibilityResultWithEpletMmRisk[],
    pairedList: IRecipientWithPairedDonor[],
    singleEpletMmRisk: ISingleEpletMmRisk,
    lkdpiInfo: ILkdpiInfo,
    donorCompatibilityResult: ICompatibilityResult,
    avoids: any[],
    confirmReplace: {
        show: boolean,
        compatibilityResultsRecipient: IRecipient
    },
    confirmPairRequest: {
        show: boolean,
        recipientWithPairedDonor: IRecipientWithPairedDonor
    }
}

class WaitlistReplace extends React.Component<TProps, IState> {
    readonly state: IState = {
        recipient: null,
        donor: null,
        waitList: [],
        pairedList: [],
        lkdpiInfo: null,
        singleEpletMmRisk: null,
        donorCompatibilityResult: null,
        avoids: [],
        confirmReplace: {
            show: false,
            compatibilityResultsRecipient: null
        },
        confirmPairRequest: {
            show: false,
            recipientWithPairedDonor: null
        }
    };

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting recipient info, one moment please...').then(() => {
            Promise.all([
                this.props.getRecipient(this.props.recipientId),
                this.props.getWaitlistReplacements(this.props.centerId, this.props.recipientId)
            ])
                .then(([recipientResponse, waitlistResponse]) => {
                    let recipient = recipientResponse.data.results;

                    let { compatibilityResults, donorMatchData } = waitlistResponse.data.results;
                    let { avoids, singleEpletMmRisk, compatibilityResult: donorCompatibilityResult, lkdpiInfo, epletMmList } = donorMatchData;

                    let waitList = compatibilityResults.filter(x => x.recipient.patientTypeId == PatientType.Waitlist).map(x => {
                        let singleEpletMmRisk = epletMmList.find(e => e.donorId == x.donor.id && e.recipientId == x.recipient.id);
                        return { ...x, singleEpletMmRisk };
                    });

                    let pairList = compatibilityResults.filter(x => x.recipient.patientTypeId == PatientType.Paired);

                    let pairedList = [];

                    for (let i = 0; i < pairList.length; i++) {
                        let item = pairList[i];
                        let { recipient, epletMMRisk } = item;

                        for (let d = 0; d < recipient.pairedDonors.length; d++) {
                            let pairedDonor = recipient.pairedDonors[d];

                            let pairedRecipient = {
                                ...recipient,
                                currentDonorId: donorMatchData.compatibilityResult.donor.id,
                                epletMMRisk,
                                pairedDonorId: pairedDonor.id,
                                pairedDonorAlias: pairedDonor.alias,
                                pairedDonorAbo: pairedDonor.bloodType,
                                pairedDonorType: pairedDonor.donationTypeAbbreviation
                            };

                            pairedList.push(pairedRecipient);
                        }
                    }

                    let donor = donorMatchData.compatibilityResult != null ? donorMatchData.compatibilityResult.donor : null;

                    this.setState({
                        donor,
                        recipient,
                        waitList,
                        pairedList,
                        avoids,
                        singleEpletMmRisk,
                        donorCompatibilityResult,
                        lkdpiInfo
                    });
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleConfirmReplace = (compatibilityResultsRecipient: IRecipient) => {
        this.setState({
            confirmReplace: {
                show: true,
                compatibilityResultsRecipient,
            }
        });
    }

    handleReplace = (selectedRecipientId: number) => {
        this.setState({
            confirmReplace: {
                show: false,
                compatibilityResultsRecipient: null
            }
        }, () => {
            this.props.showLoading('Replacing patient, one moment please...').then(() => {
                this.props.replaceWaitlistRecipient(this.props.centerId, this.props.recipientId, selectedRecipientId)
                    .then((response: IBaseResponse) => {
                        let { success } = response.data;

                        if (success) {
                            window.location.href = `center/${this.props.centerId}/recipient/${selectedRecipientId}/profile/general`
                        }
                    });
            });
        });
    }

    handleCancelConfirmReplace = () => {
        this.setState({
            confirmReplace: {
                show: false,
                compatibilityResultsRecipient: null
            }
        });
    }

    handleConfirmRequest = (recipientId: number, pairedDonorId: number) => {
        let recipientWithPairedDonor = this.state.pairedList.find(x => x.id == recipientId && x.pairedDonorId == pairedDonorId);

        this.setState({
            confirmPairRequest: {
                show: true,
                recipientWithPairedDonor
            }
        });
    }

    handlePairRequest = (originalRecipientId: number, replacementRecipientId: number, bridgeDonorId: number) => {
        this.setState({
            confirmPairRequest: {
                show: false,
                recipientWithPairedDonor: null
            }
        }, () => {
            this.props.showLoading('Submitting request, one moment please...').then(() => {
                this.props.requestReplaceWaitlistRecipientWithPair(this.props.centerId, originalRecipientId, replacementRecipientId, bridgeDonorId)
                    .then((response: IBaseResponse) => {
                        let { success } = response.data;

                        if (success) {
                            window.location.href = '/center/' + this.props.centerId + '/recipient/' + replacementRecipientId + '/profile/general';
                        }
                    })
                    .catch(error => {
                        toast.error(<>We were unable to process the request.<br /><br />{error.response.data}</>, { autoClose: false });
                    })
                    .finally(() => {
                        this.props.hideLoading();
                    });
            });
        });
    }

    handleCancelConfirmRequest = () => {
        this.setState({
            confirmPairRequest: {
                show: false,
                recipientWithPairedDonor: null
            }
        });
    }

    render = () => {
        if (this.props.loading == true) {
            return <div style={{ paddingTop: 20, paddingLeft: 10, paddingRight: 10 }}>Loading data...</div>;
        }

        if (this.state.recipient == null) {
            return (
                <div style={{ width: '100%', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', minHeight: '70vh', paddingTop: 20, paddingLeft: 10, paddingRight: 10, backgroundColor: '#FFF' }}>
                    RecipientId is required to view Replacement list.
                </div>
            );
        } else if (this.state.waitList == null) {
            return (
                <div style={{ width: '100%', borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd', minHeight: '70vh', paddingTop: 20, paddingLeft: 10, paddingRight: 10, backgroundColor: '#FFF' }}>
                    Invalid Waitlist
                </div>
            );
        }

        return (
            <div style={{ padding: 10 }}>
                <RecipientNav
                    activeKey={'WAITLIST-REPLACE'}
                    isAdmin={false}
                    isNkrLogin={false}
                    centerId={this.props.centerId}
                    recipientId={this.props.recipientId}
                    recipientAlias={this.state.recipient.alias}
                />

                <div style={{ width: '100%', minHeight: '70vh', paddingTop: 20, paddingLeft: 10, paddingRight: 10, backgroundColor: '#FFF' }}>
                    <div className='container-fluid'>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div>
                                <h3 style={{ margin: 0 }}>Replace Recipient: {this.state.recipient.alias}</h3>
                            </div>
                            <div>
                                <a className='btn btn-primary btn-sm pull-right' href={`center/${this.props.centerId}/recipient/${this.props.recipientId}/profile/general`}>
                                    Cancel Replace
                                </a>
                            </div>
                        </div>

                        <div className='row'>
                            <div className='col-md-12'>
                                <WaitlistReplaceCurrentDonor
                                    centerId={this.props.centerId}
                                    recipientId={this.props.recipientId}
                                    recipient={this.state.recipient}
                                    donor={this.state.donor}
                                    donorCompatibilityResult={this.state.donorCompatibilityResult}
                                    singleEpletMmRisk={this.state.singleEpletMmRisk}
                                    lkdpiInfo={this.state.lkdpiInfo}
                                    avoids={this.state.avoids}
                                />
                            </div>
                        </div>

                        <hr />

                        <WaitListGrid
                            data={this.state.waitList}
                            placeHolderRecipient={this.state.recipient}
                            onConfirm={this.handleConfirmReplace} />

                        {/* <hr />

                        <PairGrid
                            data={this.state.pairedList}
                            placeHolderRecipient={this.state.recipient}
                            onConfirm={this.handleConfirmRequest} /> */}
                    </div>
                </div>

                <ConfirmWaitlistReplace
                    show={this.state.confirmReplace.show}
                    compatibilityResultsRecipient={this.state.confirmReplace.compatibilityResultsRecipient}
                    placeHolderRecipient={this.state.recipient}
                    onConfirm={this.handleReplace}
                    onCancel={this.handleCancelConfirmReplace}
                />

                <ConfirmPairRequest
                    show={this.state.confirmPairRequest.show}
                    recipientWithPairedDonor={this.state.confirmPairRequest.recipientWithPairedDonor}
                    placeHolderRecipient={this.state.recipient}
                    onConfirm={this.handlePairRequest}
                    onCancel={this.handleCancelConfirmRequest} />
            </div>
        );
    };
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        loading: appState.app.loading
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getRecipient: (recipientId) => {
            return dispatch(RecipientActionCreators.get(recipientId));
        },
        getWaitlistReplacements: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getWaitlistReplacements(centerId, recipientId));
        },
        replaceWaitlistRecipient: (centerId, placeHolderRecpId, selectedRecpId) => {
            return dispatch(RecipientActionCreators.replaceWaitlistRecipient(centerId, placeHolderRecpId, selectedRecpId));
        },
        requestReplaceWaitlistRecipientWithPair: (centerId, originalRecipientId, replacementRecipientId, bridgeDonorId) => {
            return dispatch(RecipientActionCreators.requestReplaceWaitlistRecipientWithPair(centerId, originalRecipientId, replacementRecipientId, bridgeDonorId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps, mapDispatchToProps)(WaitlistReplace)